class PolicyPage extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this.body = this.querySelector('[data-policy-body]');
    this.tocLists = [...this.querySelectorAll('[data-policy-toc-list]')];
    this.headings = this.body ? [...this.body.querySelectorAll('h2')] : [];

    if (!this.body || this.headings.length === 0) {
      this.tocLists.forEach((list) => list.closest('aside, details')?.setAttribute('hidden', ''));
      return;
    }

    this.buildTableOfContents();
    this.bindIntersectionObserver();
    this.rewriteLegacyPolicyLinks();
    this.bindPolicyNavigation();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.navigationController?.abort();
    window.clearTimeout(this.navigationTimer);
  }

  slugify(value, index) {
    const base = value
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    return base || `section-${index + 1}`;
  }

  buildTableOfContents() {
    const usedIds = new Set();

    this.headings.forEach((heading, index) => {
      const label = heading.textContent.trim();
      let id = heading.id || this.slugify(label, index);
      let suffix = 2;

      while (usedIds.has(id)) {
        id = `${this.slugify(label, index)}-${suffix}`;
        suffix += 1;
      }

      heading.id = id;
      usedIds.add(id);
    });

    this.tocLists.forEach((list) => {
      const fragment = document.createDocumentFragment();

      this.headings.forEach((heading) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();
        link.dataset.policyTocLink = heading.id;
        item.append(link);
        fragment.append(item);
      });

      list.replaceChildren(fragment);
    });
  }

  bindIntersectionObserver() {
    const setActive = (id) => {
      this.querySelectorAll('[data-policy-toc-link]').forEach((link) => {
        const isActive = link.dataset.policyTocLink === id;
        link.classList.toggle('is-active', isActive);
        if (isActive) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    };

    setActive(this.headings[0].id);

    this.observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-18% 0px -68% 0px', threshold: [0, 1] }
    );

    this.headings.forEach((heading) => this.observer.observe(heading));
  }

  rewriteLegacyPolicyLinks() {
    const routes = {
      '/policies/privacy-policy': '/pages/privacy-policy',
      '/policies/shipping-policy': '/pages/shipping-policy',
      '/policies/refund-policy': '/pages/returns-refunds',
      '/policies/terms-of-service': '/pages/terms-of-service',
      '/policies/contact-information': '/pages/contact-information',
    };

    this.body.querySelectorAll('a[href]').forEach((link) => {
      const url = new URL(link.href, window.location.origin);
      const replacement = routes[url.pathname];
      if (replacement) link.href = `${replacement}${url.hash}`;
    });
  }

  bindPolicyNavigation() {
    this.navigationController = new AbortController();

    this.querySelector('.policy-page__policy-nav')?.addEventListener(
      'click',
      (event) => {
        const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
        if (
          !link ||
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          link.target === '_blank'
        ) {
          return;
        }

        const destination = new URL(link.href, window.location.href);
        if (destination.origin !== window.location.origin || destination.href === window.location.href) return;

        event.preventDefault();
        this.classList.add('is-leaving');
        this.setAttribute('aria-busy', 'true');

        this.navigationTimer = window.setTimeout(() => {
          window.location.assign(destination.href);
        }, 180);
      },
      { signal: this.navigationController.signal }
    );
  }
}

if (!customElements.get('policy-page')) {
  customElements.define('policy-page', PolicyPage);
}
